/**
 * Desktop is 900 px and up (see tokens.css `--bp-desktop`), and since D-123 also a phone on its side that is at least
 * 640 px wide: the pages use their wide layouts there, made compact by `PHONE_LANDSCAPE_QUERY` in CSS. The play screens
 * (table, waiting room, results, watching) check `usePhoneLandscape` first and use their own landscape layout.
 * SSR- and jsdom-safe: no matchMedia means "not desktop".
 */
import { useEffect, useState } from "react";

/** A phone on its side: landscape and at most 500 px tall, whatever its width (a big phone is over 900 px wide). */
export const PHONE_LANDSCAPE_QUERY = "(orientation: landscape) and (max-height: 500px)";
export const DESKTOP_QUERY = "(min-width: 900px), (orientation: landscape) and (max-height: 500px) and (min-width: 640px)";

export function isDesktopNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(DESKTOP_QUERY).matches;
}

export function useDesktop(): boolean {
  const [desktop, setDesktop] = useState<boolean>(isDesktopNow);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const on = () => setDesktop(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return desktop;
}

export function isPhoneLandscapeNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(PHONE_LANDSCAPE_QUERY).matches;
}

/** D-123: true on a phone on its side (see `PHONE_LANDSCAPE_QUERY`). */
export function usePhoneLandscape(): boolean {
  const [on, setOn] = useState<boolean>(isPhoneLandscapeNow);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(PHONE_LANDSCAPE_QUERY);
    const f = () => setOn(mq.matches);
    f();
    mq.addEventListener?.("change", f);
    return () => mq.removeEventListener?.("change", f);
  }, []);
  return on;
}

/** On a phone on its side the played lessons and the round review fill the screen like the table: no rail (D-125). */
const LAND_FULL = /^\/(learn\/(turn|groups|digu)|review\/[^/]+)\/?$/;
export const fullScreenSideways = (pathname: string): boolean => LAND_FULL.test(pathname);
